import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { FileIcon } from '@icons/FileIcon'

import { Body2 } from '@ui/Typography'
import { DocumentLink } from '@ui/v2/DocumentLink/DocumentLink'
import { LinkIcon } from '@ui/v2/Icons/LinkIcon'
import { PageSection } from '@ui/v2/PageSection/PageSection'

import { useGAEvents } from '@utils/callGAEvents'
import { useWindowResize } from '@utils/domUtils'

import { ExternalLink } from '@global/ExternalLink'

import { useLinksConfig } from '@hooks/useLinksConfig'
import { useScrollToTop } from '@hooks/useScrollToTop'

import styles from './SocialTrading.module.scss'

export const SocialTradingPage = () => {
  useScrollToTop()

  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const { callGAEvents } = useGAEvents()
  const linksProps = useLinks()
  const { registrationURL, providerRegistrationURL, loginURL, exploreStrategiesLink } = linksProps
  const { userGuideLink } = linksProps

  const handleClick = (url: string, callback: () => void) => () => {
    window.open(url, '_blank', 'noopener,noreferrer')
    callback?.()
  }

  return (
    <div className={styles.section}>
      <div className={styles.title}>
        <PageSection icon={FileIcon} title={t('IB.SocialTradingPage.How to Get Started')} />
      </div>
      <div className={styles.instructionList}>
        <div className={styles.contentWrapper}>
          <h3>{t('IB.SocialTradingPage.For Provider')}</h3>
          <ul className={styles.contentList}>
            <li className={styles.item}>
              <Body2>
                {t('IB.SocialTradingPage.Open a Trading Account with with a minimum balance')}
              </Body2>
            </li>
            <li className={styles.item}>
              <Body2>
                <Trans
                  i18nKey='IB.SocialTradingPage.Register as a provider and choose the trading account'
                  components={{
                    a: (
                      <ExternalLink
                        url={userGuideLink}
                        onClick={handleClick(userGuideLink, () =>
                          callGAEvents().SocialTradingPage.Links.userGuideProvider()
                        )}
                      />
                    ),
                  }}
                />
              </Body2>
            </li>
            <li className={styles.item}>
              <Body2>
                {t('IB.SocialTradingPage.Select the performance fee you want to charge')}
              </Body2>
            </li>
          </ul>
        </div>
        <div className={styles.contentWrapper}>
          <h3>{t('IB.SocialTradingPage.For Follower')}</h3>
          <ul className={styles.contentList}>
            <li className={styles.item}>
              <Body2>{t('IB.SocialTradingPage.Create a Trading Account')}</Body2>
            </li>
            <li className={styles.item}>
              <Body2>
                {t('IB.SocialTradingPage.Register as a follower using the MT4/MT5 credentials')}
              </Body2>
            </li>
            <li className={styles.item}>
              <Body2>
                <Trans
                  i18nKey='IB.SocialTradingPage.Choose your favourite Strategy Providers and let them do the work'
                  components={{
                    a: (
                      <ExternalLink
                        url={userGuideLink}
                        onClick={handleClick(userGuideLink, () =>
                          callGAEvents().SocialTradingPage.Links.userGuideFollower()
                        )}
                      />
                    ),
                  }}
                />
              </Body2>
            </li>
          </ul>
        </div>
      </div>
      <div className={styles.title}>
        <PageSection icon={LinkIcon} title={t('IB.SocialTradingPage.Links')} />
      </div>
      {isMobile ? (
        // mobile order
        <div className={styles.linkList}>
          <DocumentLink
            title={t('IB.SocialTradingPage.Login')}
            onClick={handleClick(loginURL, () => callGAEvents().SocialTradingPage.Links.login())}
          />
          <DocumentLink
            title={t('IB.SocialTradingPage.Explore Strategies')}
            onClick={handleClick(exploreStrategiesLink, () =>
              callGAEvents().SocialTradingPage.Links.strategyProviders()
            )}
          />
          <DocumentLink
            title={t('IB.SocialTradingPage.Follower Registration')}
            onClick={handleClick(registrationURL, () =>
              callGAEvents().SocialTradingPage.Links.followerRegistration()
            )}
          />
          <DocumentLink
            title={t('IB.SocialTradingPage.Provider Registration')}
            onClick={handleClick(providerRegistrationURL, () =>
              callGAEvents().SocialTradingPage.Links.providerRegistration()
            )}
          />
        </div>
      ) : (
        // desktop order
        <>
          <div className={styles.linkList}>
            <DocumentLink
              title={t('IB.SocialTradingPage.Login')}
              onClick={handleClick(loginURL, () => callGAEvents().SocialTradingPage.Links.login())}
            />
            <DocumentLink
              title={t('IB.SocialTradingPage.Follower Registration')}
              onClick={handleClick(registrationURL, () =>
                callGAEvents().SocialTradingPage.Links.followerRegistration()
              )}
            />
          </div>
          <div className={styles.linkList}>
            <DocumentLink
              title={t('IB.SocialTradingPage.Explore Strategies')}
              onClick={handleClick(exploreStrategiesLink, () =>
                callGAEvents().SocialTradingPage.Links.strategyProviders()
              )}
            />
            <DocumentLink
              title={t('IB.SocialTradingPage.Provider Registration')}
              onClick={handleClick(providerRegistrationURL, () =>
                callGAEvents().SocialTradingPage.Links.providerRegistration()
              )}
            />
          </div>
        </>
      )}
    </div>
  )
}

const useLinks = () => {
  const { links } = useLinksConfig()
  const locale = useSessionLanguage()

  const createURL = (baseUrl: string) => {
    return `${baseUrl}?lang=${locale}`
  }

  return useMemo(
    () => ({
      userGuideLink: links.socialTrading.UserGuide,
      exploreStrategiesLink: links.socialTrading.ExploreStrategies,
      loginURL: createURL(links.socialTrading.Login),
      registrationURL: createURL(links.socialTrading.FollowerRegistration),
      providerRegistrationURL: createURL(links.socialTrading.ProviderRegistration),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale]
  )
}
